import {
    memo,
    useEffect,
    useState,
    useRef,
    useImperativeHandle,
    useMemo,
    type SetStateAction,
    forwardRef,
    useCallback,
} from 'react';
import classNames from 'classnames';
import type { FormikErrors } from 'formik';
import { useTranslation } from 'react-i18next';
import { first, isEmpty, isNil, noop } from 'lodash';
import { ReactFlowProvider, isNode, type Node } from 'reactflow';
// Import Types
import type { JourneyStepFormValues, JourneyStepRef, PredefinedOptions } from 'types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { selectPartnerSettingsSubscription } from 'store/slices/partnerSettings';
import { incrementBlockCount, setMaxBlocksCounts } from 'store/slices/customerJourney';
import { setRouteLeaveHasDataChange } from 'store/slices/routeLeaveConfirmation';
// Import UI Components
import { BusyLoader, Button, Tooltip } from '@geneui/components';
// Import Components
import FlowEditor, { type FlowEditorRef } from 'components/CustomerJourney/FlowEditor';
import FlowEditorSidebar from 'components/CustomerJourney/FlowEditorSidebar';
import CJBlockInfoSidebar from 'components/CustomerJourney/CJBlockInfoSidebar';
// Import Enums
import { ObjectDefinitionTypeEnum, PageTypesEnum } from 'enums';
// Import Constants
import {
    CustomerJourneyStatusLabels,
    PageValidationTypes,
    newVersionBuilderConfig,
    CustomerJourneyGroupBlockTypes,
    l,
    ClientsCountType,
    RealtimePromotionTypes,
} from 'constants/common';
// Import Hook
import useValidation from 'hooks/UseValidation';
import useValidator from 'hooks/UseValidator';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import { useVersionHistoryContext } from 'components/VersionHistory';
// Import Context
import { useCJConfig } from 'context/cjConfigContext';
// Import Helpers
import { isObjectsEquals } from 'services/Helpers';
import {
    groupBlocksBuilder,
    mapFlowEditorModelToConfigModel,
    mapConfigToFlowEditorModel,
    getCustomerJourneyPageCustomValidationTypeErrorText,
    getDependencies,
    elementsCorrection,
    isEditOnlyLive,
    calculateTotalClientsRemoveCount,
    getTargetClientsData,
    filterFlowNodes,
} from 'services/customerJourney/helpers';
// Import Services
import { CustomerJourneyHttpService } from 'services/http';
// Import Styles
import 'assets/scss/customerJourneyPage.scss';

interface PageInitialDataShape {
    Name: Nullable<string>;
    Description: Nullable<string>;
    Elements: unknown[];
}

const pageInitialDataShape: PageInitialDataShape = {
    Name: null,
    Description: null,
    Elements: [],
};

interface Props {
    onUpdateValues: (
        values: SetStateAction<JourneyStepFormValues>,
        shouldValidate?: boolean | undefined,
    ) => Promise<void> | Promise<FormikErrors<JourneyStepFormValues>>;
}

const JourneyPage = forwardRef<JourneyStepRef, Props>(({ onUpdateValues }, ref) => {
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();
    const dispatch = useAppDispatch();

    const cjWrapperRef = useRef<HTMLDivElement>(null);

    const {
        journeyRef,
        pageMode = PageTypesEnum.create,
        initialData = [],
        blockActions = {},
        isLoading = false,
        defaultName = null,
        defaultDescription = null,
        status = null,
        blocksCounts = null,
        blockInfo = null,
        viewPageHeadlineData,
        pushElementsTree = noop,
        isInfoSideBarOpened = false,
        infoSidebarToggleHandler = noop,
        onFlowEditorSelectionChange = noop,
        filters,
        customActions = <></>,
        promotionType,
        isPredefined = false,
        initialPredefinedOptions,
        setIsDisabledDownload,
    } = useCJConfig();

    const isViewPage = pageMode === PageTypesEnum.view;

    const Subscription = useAppSelector(selectPartnerSettingsSubscription);

    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isBlocksSidebarDisabled, setIsBlocksSidebarDisabled] = useState(true);
    const [groupBlocks, setGroupBlocks] = useState<AnyObject[]>([]);
    const [elements, setElements] = useState<Node[]>([]);
    const [initialElements, setInitialElements] = useState<Node[]>([]);
    // True after the flow editor commits its first elements, block counts can be applied to it from then on
    const [isFlowEditorReady, setIsFlowEditorReady] = useState(false);

    const [pageInitialData, setPageInitialData] = useState({ ...pageInitialDataShape });
    // Config blocks of a restored browser draft, applied to the canvas once the editor is ready
    const [pendingDraftBlocks, setPendingDraftBlocks] = useState<Nullable<unknown[]>>(null);

    const [clientsCountType, setClientsCountType] = useState(ClientsCountType.Total);
    const [showRemovedClientsCount, setShowRemovedClientsCount] = useState(
        promotionType === RealtimePromotionTypes.ActionCampaign,
    );
    // PreSave Modal States
    const [predefinedOptions, setPredefinedOptions] = useState<PredefinedOptions>(
        initialPredefinedOptions ?? { regions: [], categories: [] },
    );

    const { pointInTime } = useVersionHistoryContext();
    const isVersionViewMode = !isNil(pointInTime);

    const definitionType = useMemo(() => {
        return isPredefined ? ObjectDefinitionTypeEnum.Predefined : ObjectDefinitionTypeEnum.Regular;
    }, [isPredefined]);

    const { addValidationProperties, resetValidationScenario, clearValidationScenario } = useValidation(
        PageValidationTypes.JourneyPage,
        false,
        pageMode,
    );

    const readOnly = PageTypesEnum.view === pageMode;

    const resetClicked = useRef(false);
    // Bumped on reset, clear and every new draft apply so an in-flight draft overlay cannot land on a canvas
    // that was reset or replaced while its dependencies were still loading
    const draftOverlayVersionRef = useRef(0);

    const flowEditorRef = useRef<FlowEditorRef>(null);
    const initialCountsAppliedRef = useRef(false);

    const validator = useValidator(getCustomerJourneyPageCustomValidationTypeErrorText(t));

    useEffect(() => {
        if (pageMode !== PageTypesEnum.view && !isEmpty(Subscription?.WorkflowBlocks)) {
            validator({
                AvailableBlocks: { elements: elements, availableBlocks: Subscription?.WorkflowBlocks ?? [] },
            });
        }
        // This effect is used to validate available blocks.
        // Missing dependencies: elements, type, validator. elements were creating infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Subscription?.WorkflowBlocks]);

    useImperativeHandle(journeyRef, () => ({
        setClientsCountsByBlock: flowEditorRef.current?.setClientsCountsByBlock,
    }));

    const { getCustomerJourneySchema, getCustomerJourneyLimits } = useCancelableRequests({
        getCustomerJourneySchema: CustomerJourneyHttpService.getCustomerJourneySchema,
        getCustomerJourneyLimits: CustomerJourneyHttpService.getCustomerJourneyLimits,
    });

    useEffect(() => {
        onUpdateValues({
            elements,
            requestBody: {
                Config: JSON.stringify({ Blocks: mapFlowEditorModelToConfigModel(elements) }),
                BuilderConfig: newVersionBuilderConfig,
                Promotion: null,
                PromotionType: promotionType,
                DefinitionType: definitionType,
                ...(isPredefined && {
                    PredefinedCategories: predefinedOptions.categories,
                    PredefinedRegions: predefinedOptions.regions,
                }),
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [definitionType, elements, predefinedOptions.categories, predefinedOptions.regions]);

    useEffect(() => {
        const target = first(
            elements.filter((element) => {
                return isNode(element) && element.type === CustomerJourneyGroupBlockTypes.target;
            }),
        );
        setIsDisabledDownload(isNil(target?.data?.currentClientsCounts) || +target?.data.currentClientsCounts === 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [elements]);

    const handleToggleFullScreen = useCallback(() => {
        if (!document.fullscreenElement) {
            cjWrapperRef?.current?.requestFullscreen().catch((err) => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
            });
        } else {
            document.exitFullscreen();
        }
    }, []);

    // Sync with browser full-screen state (e.g., if user presses ESC)
    useEffect(() => {
        const toggleFullScreen = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', toggleFullScreen);

        return () => {
            document.removeEventListener('fullscreenchange', toggleFullScreen);
        };
    }, []);

    const handleClear = useCallback(() => {
        resetValidationScenario();
        resetClicked.current = true;
        draftOverlayVersionRef.current += 1;
        flowEditorRef.current?.clearFlowEditor?.();
    }, [resetValidationScenario]);

    const handleReset = useCallback(() => {
        resetValidationScenario();
        resetClicked.current = true;
        draftOverlayVersionRef.current += 1;
        flowEditorRef.current?.resetFlowEditor?.();
        setPageInitialData((prev) => ({ ...prev, Elements: mapFlowEditorModelToConfigModel(initialElements) }));
    }, [initialElements, resetValidationScenario]);

    const handleApplyDraft = useCallback((configBlocks: unknown[]) => {
        draftOverlayVersionRef.current += 1;
        setPendingDraftBlocks(configBlocks);
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            onClear: handleClear,
            onReset: handleReset,
            onApplyDraft: handleApplyDraft,
        }),
        [handleApplyDraft, handleClear, handleReset],
    );

    const init = () => {
        doGetRequest(getCustomerJourneySchema.request, {
            queryString: {
                promotionType: promotionType,
            },
            successCallback: (data) => {
                setGroupBlocks(groupBlocksBuilder(data));
                if (pageMode !== PageTypesEnum.view) {
                    doGetRequest(getCustomerJourneyLimits.request, {
                        successCallback: (data) => {
                            let limit, count;

                            if (promotionType === RealtimePromotionTypes.CustomerJourney) {
                                limit = data.WorkflowLimit;
                                count = data.WorkflowCount;
                            } else if (promotionType === RealtimePromotionTypes.ActionCampaign) {
                                limit = data.InstantWorkflowLimit;
                                count = data.InstantWorkflowCount;
                            }
                            addValidationProperties({
                                CustomerJourneyLimit: { limit: limit, count: count },
                            });
                            dispatch(setMaxBlocksCounts(data.BlockLimit));
                        },
                    }).then(() => setIsBlocksSidebarDisabled(false));
                }
            },
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    const getInitialElements = (initialData, groupBlocks, nullableBlocksCounts) => {
        // Block counts are loaded separately and may still be pending, elements are built without them then
        const blocksCounts = nullableBlocksCounts ?? {};
        const tmpInitialElements = mapConfigToFlowEditorModel(initialData, groupBlocks).map(
            (element, _index, allElements) => {
                if (isNode(element)) {
                    element.data.readOnly = readOnly;
                    if (readOnly) {
                        const { targetClientsCount, targetClientsRemoveCount } = getTargetClientsData(
                            blocksCounts,
                            allElements,
                        );

                        element.data.actions = blockActions;
                        element.data.allClientsCounts = blocksCounts[element.id]?.OutputModelsCount;
                        element.data.removeClientsCounts = blocksCounts[element.id]?.RemovedModelsCount;
                        element.data.currentClientsCounts = blocksCounts[element.id]?.OutputModelsCount;
                        element.data.workingState = blocksCounts[element.id]?.WorkingState;
                        element.data.blockWorkingState = initialData.find(
                            ({ Name }) => Name === element.id,
                        )?.BlockWorkingState;
                        element.data.customerJourneyStatus = viewPageHeadlineData?.Status;
                        element.data.totalClientsCount = blocksCounts[element.id]?.TotalModelsCount;
                        element.data.totalClientsRemoveCount = calculateTotalClientsRemoveCount(
                            element,
                            allElements,
                            blocksCounts,
                        );
                        element.data.isFiltered = false;
                        element.data.clientsCountType = clientsCountType;
                        element.data.showRemovedClientsCount = showRemovedClientsCount;
                        element.data.targetClientsCount = targetClientsCount;
                        element.data.targetClientsRemoveCount = targetClientsRemoveCount;
                    } else {
                        element.data.actions = {
                            editNameAction(args) {
                                flowEditorRef.current?.editNameAction?.(args);
                            },
                            deleteAction(args) {
                                flowEditorRef.current?.deleteAction?.(args);
                            },
                            editAction(args) {
                                flowEditorRef.current?.editAction?.(args);
                            },
                            cloneAction(args) {
                                flowEditorRef.current?.cloneAction?.(args);
                            },
                            refreshElements() {
                                flowEditorRef.current?.refreshElements?.();
                            },
                        };
                        if (isEditOnlyLive(pageMode, status)) {
                            element.data.allClientsCounts = blocksCounts[element.id]?.OutputModelsCount;
                            element.data.workingState = blocksCounts[element.id]?.WorkingState;
                            element.data.blockWorkingState = initialData.find(
                                ({ Name }) => Name === element.id,
                            )?.BlockWorkingState;
                            element.data.customerJourneyStatus = status;
                            element.data.isDeletable = element.type !== CustomerJourneyGroupBlockTypes.target;
                            element.data.isEditOnlyLive = true;
                        } else {
                            element.data.isDeletable = true;
                        }
                        element.data.isCloneable = element.type !== CustomerJourneyGroupBlockTypes.target;
                    }
                }
                return element;
            },
        );

        return getDependencies(tmpInitialElements, pageMode).then(() => tmpInitialElements);
    };

    const setInitialVisibleBlocksCounts = (initialElements) => {
        const initialVisibleBlocksCounts = filterFlowNodes(initialElements).reduce((acc, item) => {
            if (isNil(acc[item.type ?? ''])) {
                acc[item.type ?? ''] = 1;
            } else {
                acc[item.type ?? '']++;
            }
            return acc;
        }, {});
        dispatch(incrementBlockCount(initialVisibleBlocksCounts));
    };

    useEffect(() => {
        if (!isEmpty(groupBlocks) && !isEmpty(initialData)) {
            const isEditOrClone = pageMode === PageTypesEnum.edit || pageMode === PageTypesEnum.clone;
            // The canvas does not wait for the block counts, they are applied to the mounted editor once loaded
            getInitialElements(initialData, groupBlocks, blocksCounts).then((initialElements) => {
                // Rebuilt elements need the first loaded counts applied again if they were not available yet
                initialCountsAppliedRef.current = false;
                isEditOrClone && setInitialVisibleBlocksCounts(initialElements);
                setElements(initialElements);
                setInitialElements(initialElements);
                setPageInitialData((prev) => ({
                    ...prev,
                    Elements: mapFlowEditorModelToConfigModel(initialElements),
                }));
            });
        }

        // DEPS: [blocksCounts, getInitialElements, pageMode] — counts are applied separately by the effect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData, groupBlocks]);

    // Apply the first loaded block counts to the mounted editor. Later counts updates (refresh, filters) are pushed
    // by the pages themselves through `setClientsCountsByBlock` with the proper filtered flags.
    useEffect(() => {
        if (!isFlowEditorReady || isNil(blocksCounts) || initialCountsAppliedRef.current) {
            return;
        }

        initialCountsAppliedRef.current = true;
        flowEditorRef.current?.setClientsCountsByBlock?.(blocksCounts, true);
    }, [blocksCounts, isFlowEditorReady]);

    // Overlay a restored draft on the canvas. Waits for the block schema and, outside create mode, for the
    // backend elements to be mounted first, so `initialElements` / `pageInitialData` stay the reset baseline.
    useEffect(() => {
        if (isNil(pendingDraftBlocks) || isEmpty(groupBlocks)) {
            return;
        }

        const isEditorReady = pageMode === PageTypesEnum.create || !isEmpty(initialElements);
        if (!isEditorReady || isNil(flowEditorRef.current)) {
            return;
        }

        setPendingDraftBlocks(null);
        const overlayVersion = draftOverlayVersionRef.current;
        getInitialElements(pendingDraftBlocks, groupBlocks, blocksCounts).then((draftElements) => {
            if (overlayVersion !== draftOverlayVersionRef.current) {
                return;
            }
            flowEditorRef.current?.replaceElements?.(draftElements);
        });
        // DEPS: [getInitialElements, pageMode] — getInitialElements is a plain closure recreated every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingDraftBlocks, groupBlocks, initialElements, blocksCounts]);

    useEffect(() => {
        flowEditorRef.current?.setBlockCountCalculationTypes?.(clientsCountType, showRemovedClientsCount);
    }, [clientsCountType, showRemovedClientsCount]);

    const cleanUp = () => {
        return () => {
            setElements([]);
            clearValidationScenario();
            setPageInitialData({ ...pageInitialDataShape });
        };
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    const getElementsUpdate = (newData) => {
        if (pageMode !== PageTypesEnum.view) {
            validator({
                CustomerJourney: newData,
                AvailableBlocks: { elements: newData, availableBlocks: Subscription?.WorkflowBlocks ?? [] },
            });
        }
        setElements(newData);
        pushElementsTree(newData);
        setIsFlowEditorReady(true);
    };

    useEffect(() => {
        setPageInitialData((prev) => ({ ...prev, Name: defaultName }));
    }, [defaultName]);

    useEffect(() => {
        setPageInitialData((prev) => ({ ...prev, Description: defaultDescription }));
    }, [defaultDescription]);

    useEffect(() => {
        initialPredefinedOptions && setPredefinedOptions(initialPredefinedOptions);
    }, [initialPredefinedOptions]);

    useEffect(() => {
        if (isViewPage) {
            dispatch(setRouteLeaveHasDataChange(false));
            return;
        }

        const hasDataChange = !isObjectsEquals(
            {
                Elements: mapFlowEditorModelToConfigModel(elements),
            },
            {
                Elements: pageInitialData.Elements,
            },
        );

        dispatch(setRouteLeaveHasDataChange(hasDataChange));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, elements, pageInitialData, isViewPage]);

    return (
        <div ref={cjWrapperRef} className="crm-customer-journey-cecp-page">
            <div
                className={classNames('crm-page-wrapper', {
                    'crm-customer-journey-view-page-wrapper': isViewPage,
                    'crm-page-wrapper-with-client-list':
                        !isEmpty(viewPageHeadlineData) &&
                        viewPageHeadlineData.Status !== +CustomerJourneyStatusLabels.NonStarted &&
                        !isVersionViewMode &&
                        promotionType !== RealtimePromotionTypes.ActionCampaign,
                    'crm-page-full-screen': isViewPage && isFullScreen,
                })}
            >
                {isViewPage ? (
                    <CJBlockInfoSidebar
                        elements={elements}
                        blockInfoData={blockInfo ?? {}}
                        isDisabled={isEmpty(blockInfo)}
                        isInfoSideBarOpened={isInfoSideBarOpened}
                        infoSidebarToggleHandler={infoSidebarToggleHandler}
                    />
                ) : (
                    <FlowEditorSidebar
                        isDisabled={isBlocksSidebarDisabled}
                        groupBlocks={groupBlocks}
                        defaultOpened={pageMode === PageTypesEnum.create}
                    />
                )}

                <div className="crm-section">
                    {pageMode === PageTypesEnum.create ? (
                        <ReactFlowProvider>
                            <FlowEditor
                                ref={flowEditorRef}
                                pushElementsUpdate={getElementsUpdate}
                                initialElements={elements}
                                elementsCorrection={elementsCorrection}
                                isFullScreen={isFullScreen}
                                onToggleFullScreen={handleToggleFullScreen}
                                onSelectionChange={onFlowEditorSelectionChange}
                                customActions={customActions}
                            />
                        </ReactFlowProvider>
                    ) : (
                        <BusyLoader
                            // TODO: create new state for initial elements loading and replace initial elements isEmpty checking
                            isBusy={(isEmpty(initialElements) && !resetClicked.current) || isLoading}
                            type="spinner"
                            spinnerSize="big"
                        >
                            {(resetClicked.current || !isEmpty(initialElements)) && (
                                <ReactFlowProvider>
                                    <FlowEditor
                                        ref={flowEditorRef}
                                        pushElementsUpdate={getElementsUpdate}
                                        initialElements={initialElements}
                                        elementsCorrection={elementsCorrection}
                                        readOnly={readOnly}
                                        isFullScreen={isFullScreen}
                                        onToggleFullScreen={handleToggleFullScreen}
                                        onSelectionChange={onFlowEditorSelectionChange}
                                        customerJourneyStatus={viewPageHeadlineData?.Status}
                                        customActions={
                                            viewPageHeadlineData?.Status === +CustomerJourneyStatusLabels.NonStarted ||
                                            isVersionViewMode ? (
                                                <></>
                                            ) : (
                                                <>
                                                    {customActions}
                                                    {readOnly &&
                                                        viewPageHeadlineData?.Status !==
                                                            +CustomerJourneyStatusLabels.NonStarted &&
                                                        !isVersionViewMode && (
                                                            <>
                                                                {promotionType !==
                                                                    RealtimePromotionTypes.ActionCampaign && (
                                                                    <Tooltip text={t(l.ShowTotalClientsIncludeRemoved)}>
                                                                        <Button
                                                                            icon="bc-icon-review"
                                                                            className={classNames({
                                                                                active: showRemovedClientsCount,
                                                                                'new-section': true,
                                                                            })}
                                                                            onClick={() => {
                                                                                setShowRemovedClientsCount(
                                                                                    !showRemovedClientsCount,
                                                                                );
                                                                            }}
                                                                            disabled={!isNil(filters)}
                                                                        />
                                                                    </Tooltip>
                                                                )}
                                                                <Tooltip text={t(l.TotalPercentageCalculation)}>
                                                                    <Button
                                                                        icon="bc-icon-percentage-left"
                                                                        className={classNames({
                                                                            active:
                                                                                clientsCountType ===
                                                                                ClientsCountType.Total,
                                                                            'new-section': true,
                                                                        })}
                                                                        onClick={() => {
                                                                            setClientsCountType(ClientsCountType.Total);
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip text={t(l.TargetPercentageCalculation)}>
                                                                    <Button
                                                                        icon="bc-icon-persentage-right"
                                                                        className={classNames({
                                                                            active:
                                                                                clientsCountType ===
                                                                                ClientsCountType.Target,
                                                                        })}
                                                                        onClick={() => {
                                                                            setClientsCountType(
                                                                                ClientsCountType.Target,
                                                                            );
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                </>
                                            )
                                        }
                                    />
                                </ReactFlowProvider>
                            )}
                        </BusyLoader>
                    )}
                </div>
            </div>
        </div>
    );
});

JourneyPage.displayName = 'JourneyPage';

export default memo(JourneyPage);
